import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Typography,
  Paper,
  AlertTitle,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import { useLocation } from "react-router-dom";

import {
  fetchpathology,
  fetchRoles,
  createAdminUser,
  updateAdminUser,
  getAdminUser,
  fetchAllDistrict,
  fetchAllRegions,
  validateUsername,
} from "../../action";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CommonSnackbar from "../../reusableComponents/components/generics/CommonSnackbar";
import { decodeId } from "../../utils/helper";

import SimpleTextField from "../../pickers/SimpleTextField";
import RolePickers from "../../pickers/RolePicker";

import PickerAutocomplete from "../../pickers/PickerAutocomplete";

import {
  ADMIN_USERS,
  FOSA_ROLES_ALLOWED,
  DEFAULT_FOSA_USER_PERMISSION,
} from "../../utils/constant";

const StyledPaper = styled(Paper)(({ theme }) => ({
  ...theme.paper.paper,
}));

const StyledPaperHeader = styled(Grid)(({ theme }) => ({
  ...theme.paper.header,
  fontSize: 600,
  padding: ".8rem",
}));

const AdminForm = () => {
  const SignInSuccess = useSelector((state) => state);

  const [fieldData, setFieldData] = useState({
    userId: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    region: null,
    city: null,
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const idWithPrefix = pathname.split("/").pop();
  const url = idWithPrefix.split("=")[1];
  const [roles, setRoles] = useState([]);
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  const curentUserId = useSelector(
    (state) => state.profileReducer?.currentUserResponseData?.id
  );

  const userRoles = useSelector((state) => state.fosaReducer?.roles);
  const editAdminUser = useSelector((state) => state?.fosaReducer?.adminItem);
  const createUpdateAdminSuccess = useSelector(
    (state) => state?.fosaReducer?.createUpdateAdminSuccess
  );
  const errorEditAdmin = useSelector(
    (state) => state?.fosaReducer?.errorEditAdmin
  );
  const regionData = useSelector(
    (state) => state?.profileReducer?.locationResponseData
  );

  const districtData = useSelector(
    (state) => state?.profileReducer?.locationDistrictResponseData
  );

  const usernameValidation = useSelector(
    (state) => state?.profileReducer?.userValidationDetail
  );

  let healthData = SignInSuccess?.fosaReducer?.pickerHealthFacilityInsurees[0];

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = async (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const setDefaultRoles = (defaultValue) => {
    const indexDefaultValue = userRoles.findIndex(
      (r) => r.name === DEFAULT_FOSA_USER_PERMISSION
    );

    if (indexDefaultValue === -1) {
      return defaultValue;
    }

    if (defaultValue.length === 0) {
      return [userRoles[indexDefaultValue]];
    }

    const value = [...defaultValue];
    if (!value.find((v) => v.name === DEFAULT_FOSA_USER_PERMISSION)) {
      value.push(userRoles[indexDefaultValue]);
    }

    return value;
  };

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchAllRegions());
    dispatch(fetchpathology());
  }, []);

  useEffect(() => {});

  useEffect(() => {
    if (url) {
      const id = decodeId(url);
      dispatch(getAdminUser(id));
    }
  }, []);

  useEffect(() => {
    if (url && curentUserId) {
      const id = decodeId(url);
      if (curentUserId === id) {
        setShowPasswordFields(true);
      }
    }
  }, [url, curentUserId]);

  useEffect(() => {
    if (createUpdateAdminSuccess) {
      setSnackbarOpen(true);
      setSnackbarMessage(createUpdateAdminSuccess);
      setSnackbarSeverity("success");
      // navigate(ADMIN_USERS);
    }
  }, [createUpdateAdminSuccess]);

  useEffect(() => {
    if (errorEditAdmin) {
      setSnackbarOpen(true);
      setSnackbarMessage(errorEditAdmin);
      setSnackbarSeverity("error");
    }
  }, [errorEditAdmin]);

  useEffect(() => {
    if (editAdminUser) {
      setFieldData({
        userId: editAdminUser.username,
        firstName: editAdminUser.iUser?.otherNames,
        lastName: editAdminUser.iUser?.lastName,
        email: editAdminUser.iUser?.email,
        phone: editAdminUser.iUser?.phone,
      });

      if (editAdminUser.iUser?.roles) {
        setRoles(setDefaultRoles(editAdminUser.iUser?.roles));
      }

      if (
        editAdminUser.iUser?.districts &&
        editAdminUser.iUser?.districts?.length > 0
      ) {
        setFieldData((prev) => {
          return {
            ...prev,
            city: editAdminUser.iUser?.districts.map(
              (district) => district.location
            ),
          };
        });

        setFieldData((prev) => {
          return {
            ...prev,
            region: editAdminUser.iUser?.districts[0].location?.parent,
          };
        });
      }
    }
  }, [editAdminUser]);

  const mapUserValuesToInput = (values) => {
    const input = {};

    if (values.id) {
      input["uuid"] = decodeId(values.id);
    }

    if (values.userId) {
      input["username"] = values.userId;
    }

    input["userTypes"] = ["INTERACTIVE", "CLAIM_ADMIN"];

    if (values.lastName) {
      input["lastName"] = values.lastName;
    }

    if (values.firstName) {
      input["otherNames"] = values.firstName;
    }

    if (values.phone) {
      input["phone"] = values.phone;
    }

    if (values.email) {
      input["email"] = values.email;
    }

    if (values.currentPassword) {
      input["currentPassword"] = values.currentPassword;
    }

    if (values.newPassword) {
      input["password"] = values.newPassword;
    }

    if (healthData?.id) {
      input["healthFacilityId"] = decodeId(healthData.id);
    }

    if (values.city && values.city.length > 0) {
      input["districts"] = values.city.map((r) => decodeId(r.id));
    }

    input["language"] = values.language || "en";

    if (values.roles && values.roles.length > 0) {
      input["roles"] = values.roles.map((r) => decodeId(r.id));
    }

    if (values.birthDate) {
      input["birthDate"] = values.birthDate;
    }

    if (values.address) {
      input["address"] = values.address;
    }

    if (values.substitutionOfficer?.id) {
      input["substitutionOfficerId"] = decodeId(values.substitutionOfficer.id);
    }

    if (values.worksTo) {
      input["worksTo"] = values.worksTo;
    }

    if (values.officerVillages && values.officerVillages.length > 0) {
      input["villageIds"] = values.officerVillages.map((location) =>
        decodeId(location.id)
      );
    }

    if (values.station?.id) {
      input["stationId"] = decodeId(values.station.id);
    }

    return input;
  };

  const validate = (values) => {
    const errors = {};

    if (usernameValidation === false) {
      errors.userId = "Username already exists";
    }

    if (!values.userId) {
      errors.userId = t("Field is required");
    }

    if (!values.firstName) {
      errors.firstName = t("Field is required");
    }

    if (!values.lastName) {
      errors.lastName = t("Field is required");
    }

    if (!values.email) {
      errors.email = t("Field is required");
    }

    // if (!values.city || values.city?.length === 0) {
    //   errors.city = t("Field is required");
    // }

    if (roles.length === 0) {
      errors.roles = t("Field is required");
    }

    return errors;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const data = fieldData;
    data["roles"] = roles;

    if (url) {
      data["id"] = url;
    }

    const errors = validate(data);
    setErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const payload = mapUserValuesToInput(data);
    payload["clientMutationId"] = new Date().getTime().toString();

    if (url) {
      dispatch(updateAdminUser(payload));
    } else {
      dispatch(createAdminUser(payload));
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  const checkUsername = useDebouncedCallback((value) => {
    dispatch(validateUsername(value));
  }, 1000);

  useEffect(() => {
    if (usernameValidation === false) {
      setErrors((prev) => {
        return {
          ...prev,
          userId: "Username already exists",
        };
      });
    }

    if (usernameValidation === true) {
      setErrors((prev) => {
        return {
          ...prev,
          userId: null,
        };
      });
    }
  }, [usernameValidation]);

  return (
    <Grid>
      <Grid>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={goBack}
          style={{ marginLeft: ".5rem", color: "black" }}
        >
          {t("adminManagement.backToAdmin")}
        </Button>
        <Grid>
          <Typography style={{ marginLeft: "1.2rem" }} variant="h6">
            {url ? t("adminManagement.editAdmin") : t("adminManagement.create")}
          </Typography>
        </Grid>
      </Grid>

      <form onSubmit={onSubmit}>
        <Grid>
          <StyledPaper>
            <Grid>
              <StyledPaperHeader>
                <Grid
                  item
                  alignItems="center"
                  display={"flex"}
                  justifyContent={"space-between"}
                >
                  <Typography variant="h6">
                    {t("adminManagement.details")}
                  </Typography>
                </Grid>
              </StyledPaperHeader>
              <Grid container style={{ margin: ".5rem .5rem .5rem .5rem" }}>
                <Grid item xs={4} className="common-input">
                  <SimpleTextField
                    name="userId"
                    label={t("adminManagement.form.userId")}
                    required
                    type="text"
                    error={errors.userId}
                    value={fieldData.userId}
                    onChange={(e) => {
                      setFieldData({
                        ...fieldData,
                        userId: e.target.value,
                      });
                      checkUsername(e.target.value);
                    }}
                  />
                </Grid>

                <Grid item xs={4} className="common-input">
                  <SimpleTextField
                    name="firstName"
                    label={t("adminManagement.form.firstName")}
                    required
                    type="text"
                    error={errors.firstName}
                    value={fieldData.firstName}
                    onChange={(e) => {
                      setFieldData({
                        ...fieldData,
                        firstName: e.target.value,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={4} className="common-input">
                  <SimpleTextField
                    name="lastName"
                    label={t("adminManagement.form.lastName")}
                    required
                    type="text"
                    error={errors.lastName}
                    value={fieldData.lastName}
                    onChange={(e) => {
                      setFieldData({
                        ...fieldData,
                        lastName: e.target.value,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={4} className="common-input">
                  <SimpleTextField
                    name="email"
                    label={t("adminManagement.form.email")}
                    required
                    type="text"
                    error={errors.email}
                    value={fieldData.email}
                    onChange={(e) => {
                      setFieldData({
                        ...fieldData,
                        email: e.target.value,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={4} className="common-input">
                  <SimpleTextField
                    name="phone"
                    label={t("adminManagement.form.phone")}
                    type="text"
                    error={errors.phone}
                    value={fieldData.phone}
                    onChange={(e) => {
                      setFieldData({
                        ...fieldData,
                        phone: e.target.value,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={4} className="common-input">
                  {editAdminUser?.iUser?.roles ? (
                    <RolePickers
                      key="roles-default-data"
                      defaultValue={
                        editAdminUser?.iUser?.roles.filter((role) =>
                          FOSA_ROLES_ALLOWED.includes(role.name)
                        ) ?? []
                      }
                      newValues={(data) =>
                        setRoles((prev) => setDefaultRoles([...data]))
                      }
                      error={errors.roles}
                    />
                  ) : (
                    <RolePickers
                      key="roles-without-data"
                      defaultValue={[]}
                      newValues={(data) =>
                        setRoles((prev) => setDefaultRoles([...data]))
                      }
                      error={errors.roles}
                    />
                  )}
                </Grid>

                {/* <Grid item xs={4} className="common-input">
                  <SimpleTextField
                    key="region-picker"
                    name="region"
                    component={PickerAutocomplete}
                    placeHolder="select"
                    options={regionData ?? []}
                    label={t("Department")}
                    error={errors.region}
                    value={fieldData.region}
                    required
                    newValues={(region) => {
                      setFieldData({
                        ...fieldData,
                        region: region,
                      });

                      dispatch(fetchAllDistrict(region?.uuid));
                    }}
                  />
                </Grid> */}
                {/* <Grid item xs={4} className="common-input">
                  <SimpleTextField
                    key="city-picker"
                    name="city"
                    required
                    component={PickerAutocomplete}
                    placeHolder="select"
                    multiple
                    value={fieldData.city ?? []}
                    label={t("City")}
                    error={errors.city}
                    options={districtData ?? []}
                    newValues={(city) => {
                      setFieldData({
                        ...fieldData,
                        city: [...city],
                      });
                    }}
                  />
                </Grid> */}

                {showPasswordFields && (
                  <>
                    <Grid item xs={4} className="common-input">
                      <SimpleTextField
                        name="currentPassword"
                        label={t("adminManagement.form.currentPassword")}
                        type="password"
                        value={fieldData.currentPassword}
                        onChange={(e) => {
                          setFieldData({
                            ...fieldData,
                            currentPassword: e.target.value,
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={4} className="common-input">
                      <SimpleTextField
                        name="newPassword"
                        label={t("adminManagement.form.newPassword")}
                        type="password"
                        value={fieldData.newPassword}
                        onChange={(e) => {
                          setFieldData({
                            ...fieldData,
                            newPassword: e.target.value,
                          });
                        }}
                      />
                    </Grid>

                    <Grid item xs={4} className="common-input">
                      <SimpleTextField
                        name="confirmPassword"
                        label={t("adminManagement.form.confirmPassword")}
                        type="password"
                        value={fieldData.confirmPassword}
                        onChange={(e) => {
                          setFieldData({
                            ...fieldData,
                            confirmPassword: e.target.value,
                          });
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        <Grid
          style={{
            paddingRight: "1rem",
          }}
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            style={{ float: "right" }}
          >
            {t("adminManagement.save")}
          </Button>
        </Grid>
      </form>

      <CommonSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Grid>
  );
};

export default AdminForm;
